// @ts-nocheck
import { FC, useState, useEffect, useRef } from 'react';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { ExpandMore } from '@mui/icons-material';
import { Box, TextField, Button, Typography, Divider } from '@mui/material';
import './select.scss';
import { AutoSizer } from 'react-virtualized';

interface CommonSelectProps {
  label: string;
  options: {
    value: string | number; label: string
  }[];
  value: string | number;
  onChange: (event: SelectChangeEvent<string | number>) => void;
  placeholder?: string;
  disabled?: boolean;
  searchPlaceholder?: string;
  lableWithId?:boolean;
}

const SingleSelect: FC<CommonSelectProps> = ({
  label,
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  searchPlaceholder,
  lableWithId = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 1000;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setFilteredOptions(options);
    setCurrentPage(1); // Reset to first page when options change
  }, [options]);

  // Handles the search input and filters options accordingly
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching
    
    // If query is empty, reset options. Otherwise filter by label OR value (allows searching by sitecode)
    if (query.trim() === '') {
      setFilteredOptions(options);
    } else {
      const lowercasedQuery = query.toLowerCase();
      setFilteredOptions(
        options.filter((option) => {
          const optionLabel = String(option.label || '').toLowerCase();
          const optionValue = String(option.value || '').toLowerCase();
          return optionLabel.includes(lowercasedQuery) || optionValue.includes(lowercasedQuery);
        })
      );
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredOptions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageOptions = filteredOptions.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 250);
  };

  const handleClose = () => {
    if (document.activeElement !== inputRef.current) {
      setOpen(false);
    }
  };

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    const selectedValue = event.target.value;
    // Match by stringified value to avoid type mismatches (number vs string)
    const matchedOption = options.find(option => String(option.value) === String(selectedValue));

    if (matchedOption) {
      onChange(event);
      setSearchQuery('');
      setOpen(false);
    }
  };

  const handleSelectBlur = (event: React.FocusEvent<HTMLElement>) => {
    if (
      !selectRef.current?.contains(event.relatedTarget) &&
      !inputRef.current?.contains(event.relatedTarget)
    ) {
      setOpen(false);
    }
  };
  const handleMenuItemClick = (selectedValue: string | number) => {  
    
    const event = { target: { value: selectedValue } } as SelectChangeEvent<string | number>;
    onChange(event);
    
    setSearchQuery('');
    setFilteredOptions(options);
    setCurrentPage(1); // Reset pagination
    setOpen(false);   
    
  };
  return (
    <FormControl fullWidth className={`${disabled ? 'disabled' : ''} select-search-wrapper`}>
      {label && <label>{label} <span>*</span></label>}
      <Select
        value={options && options.length > 0 ? value : ''}
        label={label}
        onChange={handleChange}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        IconComponent={ExpandMore}
        className='single-search-select'
        disabled={disabled}
        placeholder={placeholder}
        displayEmpty
        renderValue={(selected) => {        
          
          // Use the prop value, not the selected parameter which might be outdated
          const currentValue = value;
          
          if (!currentValue && currentValue !== 0) return <span style={{ color: '#666' }}>{placeholder}</span>;
          
          const selectedOption = options.find(option => 
            String(option.value) === String(currentValue) || option.value === currentValue
          );       
          
          
          return <Box>
          {selectedOption
            ? lableWithId
              ? `${selectedOption?.label} (${selectedOption?.value})`
              : selectedOption?.label
            : <span style={{ color: '#666' }}>{placeholder}</span>}
        </Box>;        
        }}
        MenuProps={{
          PaperProps: { className: 'single-select-search-menu' },
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          disableScrollLock: true,
          disablePortal: true,
        }}
        ref={selectRef}
        onBlur={handleSelectBlur}
      >
        <Box sx={{ p: 2, backgroundColor: '#fafafa', borderBottom: '1px solid #ddd' }} className="search-wrapper">
          <TextField
            inputRef={inputRef}
            fullWidth
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={searchPlaceholder || (options.length > 1000 ? 'Search recipes... (e.g., "Aam", "Rice", "Dal")' : 'Search...')}
            variant="outlined"
            size="small"
            disabled={disabled}
            onBlur={handleSelectBlur}
            autoComplete="off"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
                height: '40px',
                '& fieldset': {
                  borderColor: '#ddd'
                },
                '&:hover fieldset': {
                  borderColor: '#bbb'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2'
                }
              }
            }}
          />
          {options.length > 1000 && !searchQuery && (
            <Typography variant="body2" color="textSecondary" sx={{ 
              display: 'block', 
              mt: 1, 
              fontSize: '13px',
              fontStyle: 'italic'
            }}>
              {options.length} items available. Use search or pagination to browse.
            </Typography>
          )}
        </Box>
        
        {/* Pagination Controls - only show for large datasets */}
        {filteredOptions.length > itemsPerPage && (
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid #ddd', 
            backgroundColor: '#f8f9fa',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="body2" color="textPrimary" sx={{ fontSize: '14px', fontWeight: 500 }}>
              Showing {startIndex + 1}-{Math.min(endIndex, filteredOptions.length)} of {filteredOptions.length}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Button 
                variant="outlined"
                size="small" 
                onClick={handlePreviousPage} 
                disabled={currentPage === 1}
                sx={{ 
                  minWidth: '40px',
                  height: '36px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  borderColor: '#ddd',
                  color: currentPage === 1 ? '#ccc' : '#666',
                  '&:hover': {
                    borderColor: '#bbb',
                    backgroundColor: '#f5f5f5'
                  },
                  '&:disabled': {
                    borderColor: '#eee',
                    color: '#ccc'
                  }
                }}
              >
                ←
              </Button>
              <Typography variant="body2" sx={{ 
                fontSize: '14px', 
                fontWeight: 500,
                minWidth: '50px',
                textAlign: 'center',
                px: 1
              }}>
                {currentPage}/{totalPages}
              </Typography>
              <Button 
                variant="outlined"
                size="small" 
                onClick={handleNextPage} 
                disabled={currentPage === totalPages}
                sx={{ 
                  minWidth: '40px',
                  height: '36px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  borderColor: '#ddd',
                  color: currentPage === totalPages ? '#ccc' : '#666',
                  '&:hover': {
                    borderColor: '#bbb',
                    backgroundColor: '#f5f5f5'
                  },
                  '&:disabled': {
                    borderColor: '#eee',
                    color: '#ccc'
                  }
                }}
              >
                →
              </Button>
            </Box>
          </Box>
        )}
        
        <AutoSizer disableHeight>
          {({ width }: { width: number }) => (
            <div style={{ width }} className='list-wrap'>
              {currentPageOptions.length > 0 ? (
                currentPageOptions.map((option, i) => {
                  const isSelected = String(option.value) === String(value);
                  return (
                    <MenuItem
                      key={i + '-' + option.value}
                      value={option.value}
                      onClick={() => handleMenuItemClick(option.value)}
                      selected={isSelected}
                      sx={{
                        backgroundColor: isSelected ? '#e3f2fd !important' : 'transparent',
                        '&:hover': {
                          backgroundColor: isSelected ? '#bbdefb !important' : '#f5f5f5 !important'
                        }
                      }}
                    >
                      {option.label} {lableWithId ? '(' + (option.value) + ')' : ''}
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem disabled>No options found</MenuItem>
              )}
            </div>
          )}
        </AutoSizer>
      </Select>
    </FormControl>
  );
};

export default SingleSelect;